import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["orientation", "space", "readonly", "distance", "activeIndex", "defaultActiveIndex", "children", "style", "className", "hasTooltip", "onActiveIndexChange"];
import React, { useRef, forwardRef } from 'react';
import ClassNames from "classnames";
import { KEY_VALUES, useControlled } from '../utils';
import { Tooltip } from '../tooltip';
var Steppers = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'horizontal' : _props$orientation,
    space = props.space,
    _props$readonly = props.readonly,
    readonly = _props$readonly === void 0 ? false : _props$readonly,
    _props$distance = props.distance,
    distance = _props$distance === void 0 ? 4 : _props$distance,
    activeIndex = props.activeIndex,
    _props$defaultActiveI = props.defaultActiveIndex,
    defaultActiveIndex = _props$defaultActiveI === void 0 ? 0 : _props$defaultActiveI,
    children = props.children,
    _props$style = props.style,
    style = _props$style === void 0 ? {} : _props$style,
    className = props.className,
    _props$hasTooltip = props.hasTooltip,
    hasTooltip = _props$hasTooltip === void 0 ? true : _props$hasTooltip,
    onActiveIndexChange = props.onActiveIndexChange,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  var stepperRef = useRef(null);
  var _useControlled = useControlled(activeIndex, defaultActiveIndex),
    focusIndex = _useControlled[0],
    setFocusIndex = _useControlled[1];
  var currentIndexRef = useRef(focusIndex || 0);
  var selectStepper = function selectStepper(index) {
    var _stepperRef$current;
    if (readonly) return;
    var skipSpace = 0;
    var steps = (_stepperRef$current = stepperRef.current) === null || _stepperRef$current === void 0 ? void 0 : _stepperRef$current.querySelectorAll('.ap-progress-stepper-bar-inner');
    if (!readonly && steps && focusIndex < steps.length) {
      if (focusIndex > index) {
        skipSpace = focusIndex - index;
        if (skipSpace >= 2) {
          for (var k = focusIndex; k > index; k--) {
            steps[k].style.transitionDelay = (focusIndex - k) * 0.15 + "s";
          }
        }
      } else if (focusIndex < index) {
        skipSpace = index - focusIndex;
        if (skipSpace >= 2) {
          for (var j = focusIndex + 1; j <= index; j++) {
            steps[j].style.transitionDelay = (j - focusIndex) * 0.15 + "s";
          }
        }
      }
      onActiveIndexChange === null || onActiveIndexChange === void 0 ? void 0 : onActiveIndexChange(index);
      setFocusIndex(index);
      if (skipSpace >= 2) {
        setTimeout(function () {
          for (var _i = 0; _i < steps.length; _i++) {
            steps[_i].style.transitionDelay = 'unset';
          }
        }, skipSpace * 150);
      }
    }
  };
  var onKeyDown = function onKeyDown(event) {
    var _stepperRef$current2;
    if (readonly) return;
    var keyName = event.key;
    var stepNodes = (_stepperRef$current2 = stepperRef.current) === null || _stepperRef$current2 === void 0 ? void 0 : _stepperRef$current2.querySelectorAll('.ap-progress-stepper');
    if (!stepNodes || stepNodes.length < 2) return;
    switch (keyName) {
      case KEY_VALUES.LEFT:
      case KEY_VALUES.UP:
        event.preventDefault();
        currentIndexRef.current--;
        if (currentIndexRef.current < 0) {
          currentIndexRef.current = stepNodes.length - 1;
        }
        stepNodes[currentIndexRef.current].focus();
        setFocusIndex(currentIndexRef.current);
        break;
      case KEY_VALUES.RIGHT:
      case KEY_VALUES.DOWN:
        event.preventDefault();
        currentIndexRef.current++;
        if (currentIndexRef.current > stepNodes.length - 1) {
          currentIndexRef.current = 0;
        }
        stepNodes[currentIndexRef.current].focus();
        setFocusIndex(currentIndexRef.current);
        break;
      case KEY_VALUES.ENTER:
      case KEY_VALUES.SPACE:
        event.preventDefault();
        selectStepper(currentIndexRef.current);
        break;
    }
  };
  var onBtnFocus = function onBtnFocus(index) {
    if (index === focusIndex) {
      currentIndexRef.current = index;
      setFocusIndex(index);
    }
  };
  var renderHeader = function renderHeader() {
    var results = [];
    React.Children.forEach(children, function (child, index) {
      if (!child) {
        return;
      }
      var _child$props = child.props,
        label = _child$props.label,
        _child$props$status = _child$props.status,
        status = _child$props$status === void 0 ? 'normal' : _child$props$status,
        _child$props$trigger = _child$props.trigger,
        trigger = _child$props$trigger === void 0 ? 'hover' : _child$props$trigger,
        _child$props$hideTool = _child$props.hideTooltipOnBlur,
        hideTooltipOnBlur = _child$props$hideTool === void 0 ? true : _child$props$hideTool;
      var stepperStyle = orientation === 'horizontal' ? {
        width: space + "px"
      } : {
        height: space + "px"
      };
      var classBarInnerNames = ClassNames("ap-progress-stepper-bar-inner", {
        "bar-active": index <= focusIndex,
        "bar-warning": index <= focusIndex && status === "warning"
      });
      var classStepperBtnNames = ClassNames("ap-progress-stepper-btn ", {
        "normal-step-selected": !readonly && status === "normal",
        "warning-step-selected": index <= focusIndex && !readonly && status === "warning",
        "warning-step-readonly": index <= focusIndex && readonly && status === "warning",
        "upcoming-step-selected": index > focusIndex && !readonly
      });
      var getIcon = function getIcon(status, index) {
        if (index < focusIndex) {
          return status === 'normal' ? 'circle-checkmark' : 'circle-warning';
        } else if (index === focusIndex) {
          return status === 'normal' ? 'circle-radio' : 'circle-warning';
        } else {
          return 'circle-empty';
        }
      };
      var classIconNames = ClassNames("Appkit4-icon icon-" + getIcon(status, index) + "-outline", {
        "normal-status": index <= focusIndex && status === 'normal',
        "warning-status": index <= focusIndex && status === 'warning',
        "upcoming-status": index > focusIndex
      });
      var classBtnTextNames = ClassNames("ap-progress-stepper-btn-text", {
        "upcoming-status": index > focusIndex,
        "warning-status": index <= focusIndex && status === 'warning'
      });
      var ariaStatus;
      var ariaLabel = label;
      if (index <= focusIndex && status === 'warning') {
        ariaStatus = 'warning';
      } else if (index > focusIndex) {
        ariaStatus = 'upcoming';
      } else if (index === focusIndex) {
        ariaStatus = 'current';
      } else if (index < focusIndex) {
        ariaStatus = 'completed';
      }
      if (!ariaLabel) {
        ariaLabel = "step " + (index + 1);
      }
      results.push( /*#__PURE__*/React.createElement(React.Fragment, {
        key: index
      }, hasTooltip && label ? /*#__PURE__*/React.createElement(Tooltip, {
        id: 'tooltipDesc' + index,
        distance: distance,
        position: orientation === 'horizontal' ? 'bottom' : 'right',
        content: label ? label : '',
        trigger: trigger,
        hideTooltipOnBlur: hideTooltipOnBlur
      }, /*#__PURE__*/React.createElement("div", {
        id: "stepper-" + (label ? label : ''),
        className: "ap-progress-stepper",
        style: stepperStyle,
        role: "tab",
        "aria-describedby": 'tooltipDesc' + index,
        tabIndex: index === activeIndex ? 0 : -1,
        "aria-selected": index === activeIndex,
        "aria-controls": "panel-" + label,
        "aria-label": ariaLabel + " " + ariaStatus,
        onKeyDown: onKeyDown,
        onClick: function onClick() {
          selectStepper(index);
        },
        onFocus: function onFocus() {
          onBtnFocus(index);
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ap-progress-stepper-bar"
      }, /*#__PURE__*/React.createElement("span", {
        className: classBarInnerNames
      })), /*#__PURE__*/React.createElement("div", {
        className: classStepperBtnNames
      }, /*#__PURE__*/React.createElement("span", {
        className: classIconNames,
        "aria-hidden": true
      }), /*#__PURE__*/React.createElement("span", {
        className: classBtnTextNames
      }, label)))) : /*#__PURE__*/React.createElement("div", {
        id: "stepper-" + (label ? label : ''),
        className: "ap-progress-stepper",
        style: stepperStyle,
        role: "tab",
        "aria-describedby": 'tooltipDesc' + index,
        tabIndex: index === activeIndex ? 0 : -1,
        "aria-selected": index === activeIndex,
        "aria-controls": "panel-" + label,
        "aria-label": ariaLabel + " " + ariaStatus,
        onKeyDown: onKeyDown,
        onClick: function onClick() {
          selectStepper(index);
        },
        onFocus: function onFocus() {
          onBtnFocus(index);
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: "ap-progress-stepper-bar"
      }, /*#__PURE__*/React.createElement("span", {
        className: classBarInnerNames
      })), /*#__PURE__*/React.createElement("div", {
        className: classStepperBtnNames
      }, /*#__PURE__*/React.createElement("span", {
        className: classIconNames,
        "aria-hidden": true
      }), /*#__PURE__*/React.createElement("span", {
        className: classBtnTextNames
      }, label)))));
    });
    return results;
  };
  var renderContent = function renderContent() {
    var results = [];
    React.Children.forEach(children, function (child, index) {
      if (!child) {
        return;
      }
      var _child$props2 = child.props,
        label = _child$props2.label,
        status = _child$props2.status;
      var active = focusIndex === index;
      results.push( /*#__PURE__*/React.cloneElement(child, _extends({
        active: active,
        status: status,
        label: label,
        key: index
      }, child.props)));
    });
    return results;
  };
  var classWrapperNames = ClassNames("ap-progress-stepper-wrapper", className, {
    "is-vertical-stepper": orientation === 'vertical'
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    className: "ap-progress-stepper-container"
  }, restProps), /*#__PURE__*/React.createElement("div", {
    ref: stepperRef,
    className: classWrapperNames,
    style: style,
    role: "tablist",
    "aria-label": "progress stepper"
  }, renderHeader()), /*#__PURE__*/React.createElement("div", {
    className: "ap-progress-stepper-content"
  }, renderContent()));
});
export default Steppers;